import { useEffect, useRef, useState } from 'react';
import { api } from '../services/api';
import { trackEvent, mapApiErrorToCode } from '../services/analytics';

interface InterestModalProps {
  spotId: string;
  onClose: () => void;
  onSubmitted: (message: string) => void;
}

function isDuplicateError(message: string): boolean {
  const m = message.toLowerCase();
  return m.includes("we've already passed") || m.includes('already') || m.includes('duplicate');
}

function isRateLimitError(message: string): boolean {
  const m = message.toLowerCase();
  return m.includes('too many') || m.includes('rate');
}

export default function InterestModal({ spotId, onClose, onSubmitted }: InterestModalProps) {
  const [seekerName, setSeekerName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    trackEvent('interest_form_opened', { pin_type: 'spot' });
  }, []);

  useEffect(() => {
    if (error) {
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [error]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const hasMessage = message.trim().length > 0;
    const hasName = seekerName.trim().length > 0;
    trackEvent('interest_form_submitted', { pin_type: 'spot', has_message: hasMessage, has_name: hasName });
    try {
      const res = await api.createInterest({
        parking_spot_id: spotId,
        seeker_name: hasName ? seekerName.trim() : undefined,
        email: email.trim(),
        phone: phone.trim(),
        message: hasMessage ? message.trim() : undefined,
      });
      trackEvent('interest_form_result', { pin_type: 'spot', result: 'success' });
      onSubmitted(res.message);
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'Failed to submit interest';
      let result: 'duplicate' | 'rate_limited' | 'validation' | 'network' | 'server' | 'unknown';
      if (isDuplicateError(raw)) {
        result = 'duplicate';
      } else if (isRateLimitError(raw)) {
        result = 'rate_limited';
      } else {
        const code = mapApiErrorToCode(err);
        result = code === 'rate_limit' ? 'rate_limited' : code;
      }
      trackEvent('interest_form_result', { pin_type: 'spot', result });
      setError(
        result === 'duplicate'
          ? "We've already shared your details with the owner."
          : result === 'rate_limited'
          ? "You've sent too many interest requests. Please try again later."
          : result === 'network'
          ? 'Network error. Please check your connection and try again.'
          : raw
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[150] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden">
        <div className="flex-shrink-0 p-4 border-b flex items-center justify-between bg-gray-50 rounded-t-2xl">
          <h2 className="font-semibold text-gray-900">I'm interested</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
            className="text-gray-500 hover:text-gray-700 text-xl leading-none disabled:opacity-50"
          >
            &times;
          </button>
        </div>

        <form id="interest-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 min-h-0 space-y-4">
          <p className="text-sm text-gray-600">
            Share your contact with the owner. They'll see your name, email, and phone and you'll see theirs.
            Parksy is a discovery platform — verify details and meet in a safe place.
          </p>

          {error && (
            <div
              ref={errorRef}
              className="bg-red-50 text-red-800 text-sm p-3 rounded-lg border border-red-200"
            >
              {error}
            </div>
          )}

          <div>
            <label htmlFor="interest-name" className="block text-sm font-medium text-gray-700 mb-1.5">
              Your name <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              id="interest-name"
              type="text"
              value={seekerName}
              onChange={(e) => setSeekerName(e.target.value)}
              maxLength={120}
              disabled={submitting}
              placeholder="e.g. Ananya"
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm bg-white focus:ring-2 focus:ring-parksy-500 focus:border-transparent disabled:opacity-60"
            />
          </div>

          <div>
            <label htmlFor="interest-email" className="block text-sm font-medium text-gray-700 mb-1.5">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              id="interest-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              maxLength={255}
              disabled={submitting}
              placeholder="you@example.com"
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm bg-white focus:ring-2 focus:ring-parksy-500 focus:border-transparent disabled:opacity-60"
            />
          </div>

          <div>
            <label htmlFor="interest-phone" className="block text-sm font-medium text-gray-700 mb-1.5">
              Phone <span className="text-red-500">*</span>
            </label>
            <input
              id="interest-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              minLength={10}
              maxLength={20}
              disabled={submitting}
              placeholder="10-digit mobile"
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm bg-white focus:ring-2 focus:ring-parksy-500 focus:border-transparent disabled:opacity-60"
            />
          </div>

          <div>
            <label htmlFor="interest-message" className="block text-sm font-medium text-gray-700 mb-1.5">
              Message <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              id="interest-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={500}
              disabled={submitting}
              rows={3}
              placeholder="Hi, I'd like to know more about availability..."
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm bg-white focus:ring-2 focus:ring-parksy-500 focus:border-transparent disabled:opacity-60 resize-none"
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{message.length}/500</p>
          </div>
        </form>

        <div className="flex-shrink-0 p-4 border-t bg-gray-50 rounded-b-2xl flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-white disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="interest-form"
            disabled={submitting}
            className="flex-1 px-4 py-2.5 text-white rounded-lg text-sm font-medium bg-parksy-600 hover:bg-parksy-700 disabled:opacity-50"
          >
            {submitting ? 'Sending...' : "I'm interested"}
          </button>
        </div>
      </div>
    </div>
  );
}
